/**
 * Regra: cada contato pode ter apenas uma conversa por canal (source).
 * Chave única = (identidade do contato, canal).
 */

const phoneNorm = (v) => (v || '').replace(/\D/g, '');

/**
 * Retorna uma chave estável que identifica o "par (contato, canal)" da conversa.
 * Usado para garantir uma única conversa por contato por canal.
 * @param {Object} conv - conversa
 * @param {Function} getContactForConversation - (conv) => contact | null
 * @returns {string} chave única
 */
export function getContactChannelKey(conv, getContactForConversation) {
  if (!conv) return '';
  const channelKey = (conv.channelId ?? conv.channel_id ?? conv.source ?? '').toString().toLowerCase();
  const contact = getContactForConversation?.(conv);
  if (contact?.id) return `${contact.id}|${channelKey}`;
  if (conv.contact_id) return `${conv.contact_id}|${channelKey}`;
  const p = phoneNorm(conv.phone);
  if (p.length >= 8) return `p:${p}|${channelKey}`;
  const e = (conv.email || '').trim().toLowerCase();
  if (e) return `e:${e}|${channelKey}`;
  return `conv:${conv.id}|${channelKey}`;
}

/**
 * Remove duplicatas: mantém apenas a primeira conversa por (contato, canal).
 * Garante que na lista cada contato apareça no máximo uma vez por canal.
 * @param {Array} conversations - lista de conversas
 * @param {Function} getContactForConversation - (conv) => contact | null
 * @returns {Array} conversas deduplicadas
 */
export function dedupeConversationsByContactChannel(conversations, getContactForConversation) {
  if (!Array.isArray(conversations) || conversations.length === 0) return conversations;
  const seen = new Set();
  const result = [];
  for (const c of conversations) {
    const key = getContactChannelKey(c, getContactForConversation);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(c);
  }
  return result;
}

/**
 * Encontra a conversa existente para o par (contato, canal), se houver.
 * Use antes de criar nova conversa: se retornar conversa, abra ela em vez de criar outra.
 * @param {Array} conversations - lista de conversas
 * @param {string} contactId - id do contato
 * @param {string} channelIdOrSource - id do canal (cuid) ou nome do canal (whatsapp, web, etc.)
 * @param {Function} getContactForConversation - (conv) => contact | null
 * @returns {Object|null} conversa ou null
 */
export function findConversationByContactAndChannel(conversations, contactId, channelIdOrSource, getContactForConversation) {
  if (!Array.isArray(conversations) || !channelIdOrSource) return null;
  const sourceLower = String(channelIdOrSource).toLowerCase();
  return conversations.find((c) => {
    const contact = getContactForConversation?.(c);
    const sameContact = contact?.id === contactId || c.contact_id === contactId;
    const sameChannel =
      (c.channelId && c.channelId === channelIdOrSource) ||
      (c.channel_id && c.channel_id === channelIdOrSource) ||
      (c.source || '').toLowerCase() === sourceLower;
    return sameContact && sameChannel;
  }) || null;
}
